import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { CollectionCard as MyCollectionCard } from '@components/SongCards/CollectionCard';
import { CarouselWithHeader } from '@components/main-components/carousel/HeaderCarousel';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, useInfiniteQuery } from '@graphql/reactQuery';
import {
  CollectionCarouselFragmentDoc,
  CollectionCarouselSlidesDocument,
} from '@graphql/reactQuery';
import { formatProfileHeader } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectionCarousel on User {
    id
    collectedReleasesCount
    ...CarouselUserInfo
  }

  query CollectionCarouselSlides($userId: UUID!, $after: String) {
    user(id: $userId) {
      id
      releases: collectedReleases(
        pagination: { first: 10, after: $after }
        filter: { releaseAlbumRevealStatus: ONLY_NOT_REVEALED_ALBUMS }
      ) {
        edges {
          node {
            id
            ...CollectionCard
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

export const CollectionCarousel = ({
  userFrag,
  header,
}: {
  userFrag: FragmentType<CollectionCarouselFragmentDoc>;
  header?: string;
}) => {
  const user = getFragment(CollectionCarouselFragmentDoc, userFrag);

  const userId = user.id;

  const { orderedList: collectedReleases, loadMoreNextPage: loadMore } = useInfiniteQuery(
    CollectionCarouselSlidesDocument,
    {
      filterQueryKey: {
        userId,
      },
      getNextPageParam({ data }) {
        return (
          data.user?.releases.pageInfo.hasNextPage && {
            after: data.user.releases.pageInfo.endCursor,
          }
        );
      },
      variables({ pageParam }) {
        return {
          userId,
          after: pageParam?.after || null,
        };
      },
      list({ user }) {
        return user?.releases.edges.map(edge => edge.node);
      },
      uniq({ id }) {
        return id;
      },
      staleTime: 0,
    },
  );

  const collectedReleasesSlides = React.useMemo(() => {
    return collectedReleases.map(collectedRelease => ({
      id: collectedRelease.id,
      slide: (
        <MyCollectionCard
          userId={user.id}
          key={collectedRelease.id}
          data={collectedRelease}
          playlist={{
            type: 'HOLDER',
            associationId: user.id,
          }}
        />
      ),
    }));
  }, [collectedReleases, user.id]);

  if (!collectedReleasesSlides || collectedReleasesSlides.length === 0) {
    return null;
  }

  const subheader = formatProfileHeader({ count: user.collectedReleasesCount, text: 'Sounds' });

  return (
    <CarouselWithHeader
      user={user}
      header={header ?? 'Collection'}
      subheader={subheader}
      slides={collectedReleasesSlides}
      path="/collection"
      loadMore={loadMore}
      seeAllParams={null}
    />
  );
};
